import React, { useState } from "react";
import { Card, CardBody, Col, Container, Row, Button, Form, Label, Input, FormFeedback, Spinner } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import { postPublicPayment } from "../../helpers/backend_helper";
import { toast, ToastContainer } from "react-toastify";

// Custom styles for a premium feel
const styles = {
    pageBackground: {
        backgroundColor: "#fff8f5", // Light brand color tint
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem 0",
    },
    card: {
        borderRadius: "16px",
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        border: "none",
        overflow: "hidden",
    },
    header: {
        background: "#ffffff",
        padding: "3rem 2rem",
        textAlign: "center",
        borderBottom: "4px solid #f25e0e",
    },
    logo: {
        height: "60px",
        marginBottom: "1rem",
    },
    input: {
        borderRadius: "8px",
        padding: "12px 16px",
        border: "1px solid #e2e8f0",
        fontSize: "1rem",
        transition: "border-color 0.2s ease",
    },
    label: {
        fontWeight: "600",
        color: "#4a5568",
        marginBottom: "8px",
        fontSize: "0.9rem",
    },
    button: {
        background: "linear-gradient(135deg, #f25e0e 0%, #d14d0b 100%)",
        border: "none",
        borderRadius: "8px",
        padding: "12px",
        fontSize: "1rem",
        fontWeight: "600",
        marginTop: "1rem",
        transition: "transform 0.1s ease",
    },
    disabledInput: {
        backgroundColor: "#edf2f7",
        color: "#718096",
        cursor: "not-allowed",
    }
};

const PublicPayment = () => {
    const { uuid } = useParams();
    const [loading, setLoading] = useState(false);

    const formik = useFormik({
        initialValues: {
            amount: "",
            account_number: "",
            provider: "",
        },
        validationSchema: Yup.object({
            amount: Yup.number().required("Please Enter Your Amount"),
            account_number: Yup.string().required("Please Enter Your Mobile Number"),
            provider: Yup.string().required("Please Select a Provider"),
        }),
        onSubmit: (values) => {
            setLoading(true);
            const payload = {
                uuid: uuid,
                amount: values.amount,
                account_number: values.account_number,
                provider: values.provider,
            };

            postPublicPayment(payload)
                .then((response) => {
                    setLoading(false);
                    toast.success("Payment submitted successfully");
                    formik.resetForm();
                })
                .catch((error) => {
                    setLoading(false);
                    toast.error("Error submitting payment");
                    console.error(error);
                });
        },
    });

    return (
        <React.Fragment>
            <div style={styles.pageBackground}>
                <Container>
                    <Row className="justify-content-center">
                        <Col md={8} lg={6} xl={5}>
                            <Card style={styles.card}>
                                <div style={styles.header}>
                                    <img src="/static/s-logo-orange.png" alt="" style={styles.logo} className="rounded-circle" />
                                    <h4 className="mb-2 text-dark">Secure Payment</h4>
                                    <p className="mb-0 text-muted">Complete your transaction with Simply</p>
                                </div>
                                <CardBody className="p-4">
                                    <Form
                                        className="form-horizontal"
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            formik.handleSubmit();
                                            return false;
                                        }}
                                    >

                                        {/* UUID Display */}
                                        <div className="mb-4">
                                            <Label style={styles.label}>Transaction Reference</Label>
                                            <Input
                                                name="uuid"
                                                style={{ ...styles.input, ...styles.disabledInput }}
                                                value={uuid || ""}
                                                disabled
                                            />
                                        </div>

                                        {/* Amount Field */}
                                        <div className="mb-4">
                                            <Label style={styles.label}>Amount</Label>
                                            <div className="input-group">
                                                <span className="input-group-text border-0 bg-light fw-bold">$</span>
                                                <Input
                                                    name="amount"
                                                    style={styles.input}
                                                    placeholder="0.00"
                                                    type="number"
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    value={formik.values.amount}
                                                    invalid={formik.touched.amount && formik.errors.amount ? true : false}
                                                />
                                                {formik.touched.amount && formik.errors.amount && (
                                                    <FormFeedback type="invalid" className="d-block mt-1">
                                                        {formik.errors.amount}
                                                    </FormFeedback>
                                                )}
                                            </div>
                                        </div>

                                        {/* Mobile Number Field (account_number) */}
                                        <div className="mb-4">
                                            <Label style={styles.label}>Mobile Number</Label>
                                            <Input
                                                name="account_number"
                                                style={styles.input}
                                                placeholder="Enter Mobile Number"
                                                type="text"
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.account_number}
                                                invalid={formik.touched.account_number && formik.errors.account_number ? true : false}
                                            />
                                            {formik.touched.account_number && formik.errors.account_number && (
                                                <FormFeedback type="invalid" className="d-block mt-1">
                                                    {formik.errors.account_number}
                                                </FormFeedback>
                                            )}
                                        </div>

                                        {/* Provider Select Field */}
                                        <div className="mb-4">
                                            <Label style={styles.label}>Payment Provider</Label>
                                            <Input
                                                name="provider"
                                                type="select"
                                                style={styles.input}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.provider}
                                                invalid={formik.touched.provider && formik.errors.provider ? true : false}
                                            >
                                                <option value="" disabled>Select Provider</option>
                                                <option value="mtn">MTN Mobile Money</option>
                                                <option value="tigo">Tigo Cash</option>
                                                <option value="vodafone">Vodafone Cash</option>
                                            </Input>
                                            {formik.touched.provider && formik.errors.provider && (
                                                <FormFeedback type="invalid" className="d-block mt-1">
                                                    {formik.errors.provider}
                                                </FormFeedback>
                                            )}
                                        </div>

                                        <div className="d-grid">
                                            <Button
                                                style={styles.button}
                                                type="submit"
                                                className="btn-block shadow-sm"
                                                disabled={loading}
                                                onMouseOver={(e) => !loading && (e.target.style.transform = 'translateY(-1px)')}
                                                onMouseOut={(e) => !loading && (e.target.style.transform = 'translateY(0)')}
                                            >
                                                {loading ? <Spinner size="sm" /> : "Pay Now"}
                                            </Button>
                                        </div>
                                    </Form>
                                </CardBody>
                            </Card>
                            <div className="text-center mt-4">
                                <p className="text-muted mb-0">Powered by <span className="fw-bold">Simply</span></p>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
        </React.Fragment>
    );
};

export default PublicPayment;
